"use client";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Undo, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "default" | "password";
  onUndo?: () => void;
  showUndo?: boolean;
  onClear?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      variant = "default",
      onUndo,
      showUndo = false,
      onClear,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);

    if (variant === "password") {
      return (
        <div className='relative'>
          <input
            type={showPassword ? "text" : "password"}
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base md:text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 pr-10",
              className
            )}
            ref={ref}
            value={value}
            onChange={onChange}
            {...props}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
            onClick={() => setShowPassword((prev) => !prev)}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className='h-4 w-4 text-muted-foreground' />
            ) : (
              <Eye className='h-4 w-4 text-muted-foreground' />
            )}
            <span className='sr-only'>
              {showPassword ? "Hide password" : "Show password"}
            </span>
          </Button>
        </div>
      );
    }

    const hasValue = value !== undefined && value !== null && value !== "";
    const showButtons = (hasValue && onClear) || (showUndo && onUndo);

    return (
      <div className='relative'>
        <input
          type={type}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base md:text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50",
            showButtons && "pr-20",
            className
          )}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
        {showButtons && (
          <div className='absolute right-0 top-0 h-full flex items-center gap-1 px-1'>
            {hasValue && onClear && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
                tabIndex={-1}
              >
                <X className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Clear input</span>
              </Button>
            )}
            {showUndo && onUndo && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 hover:bg-transparent'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUndo();
                }}
                tabIndex={-1}
              >
                <Undo className='h-4 w-4 text-muted-foreground' />
                <span className='sr-only'>Undo changes</span>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
